import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { EditOutlined, HomeOutlined, QuestionCircleOutlined, SaveOutlined } from '@ant-design/icons';
import {
    Card,
    Col,
    Collapse,
    ConfigProvider,
    Form,
    Input,
    notification,
    Row,
    Select,
    Spin,
    Tooltip,
    Typography,
} from 'antd';
import { AgGridReact } from 'ag-grid-react';
import { GridApi } from 'ag-grid-community';
import enUS from 'antd/lib/locale/en_US';
import deDe from 'antd/lib/locale/de_DE';

import './SurveySupplierDocumentType.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';

import {
    DEFAULT_COL_DEF,
    getDefinitionFormatChannelHelp,
    getFormats,
    SURVEY_CUSTOMER_DOCUMENT_PARTNER_TABLE_HEADERS,
    SURVEY_DOCUMENT_CHANNEL_TABLE_HEADERS,
} from './documentTypeUtils';
import { PARTNER_TYPE } from '../../../constants';
import {
    getDefaultChannelData,
    getProcessName,
    getSurveyChannelBySurveyId,
    insertSurveyChannels,
} from './DocumentTypeService';
import { getAgGridLocalization } from '../../info-portal/components/grid/GridUtil';
import { StateContainer } from '../utilities/StateContainer';
import { showErrorWithHeaderNotification } from '../../Notification';
import { useAppSelector } from '../../../main/hooks';
import { MODE } from '../IStoredSurvey';
import { Link } from 'react-router-dom';
import { getI18n } from '../../../utils/Utils';
import Paragraph from "antd/es/typography/Paragraph";

type SurveySummaryRecord = {
    surveyId: number;
    partnerNo: string;
    partnerName: string;
    companyCode: string;
    includedSurveyIds?: string;
};

type SurveyMasterDataItem = {
    surveySummaryRecord: SurveySummaryRecord;
};

type ChannelData = {
    surveyChannelId: number;
    processCodeId: number;
    documentType: string;
    format: string;
    channel: string;
    provider?: string;
    channelDefault?: boolean;
    updatedChannel?: string;
    updatedFormat?: string;
    processCode?: string;
};

type DefaultChannelFormat = {
    format: Array<{
        formatName: string;
        channel: Array<{
            channelName: string;
            provider: string;
        }>;
    }>;
};

type ISurveySuppliersDocumentTypeProps = {
    next?: () => void;
    prev?: () => void;
    surveyMasterData?: SurveyMasterDataItem[];
    channelData?: ChannelData[];
    groupId?: string;
    onStatusUpdate?: () => void;
    stateContainer?: StateContainer;
};

const SurveyCustomerDocumentType: React.FC<ISurveySuppliersDocumentTypeProps> = ({
    surveyMasterData = [],
    onStatusUpdate,
    groupId,
}) => {
    const intl = useIntl();
    const [gridApi, setGridApi] = useState<GridApi>();
    const [loading, setLoading] = useState(false);
    const [channelOptions, setChannelOptions] = useState<string[]>([]);
    const [formatOptions, setFormatOptions] = useState<string[]>([]);
    const [channelFormatOptionData, setChannelFormatOptionData] = useState<DefaultChannelFormat>();
    const [channelData, setChannelData] = useState<ChannelData[]>();
    const [showEditPanel, setShowEditPanel] = useState(false);
    const [updatedFormat, setUpdatedFormat] = useState<string>();
    const [updatedChannel, setUpdatedChannel] = useState<string>();
    const [selectedProvider, setSelectedProvider] = useState<string>();
    const [form] = Form.useForm();

    const surveyState = useAppSelector((state) => state.survey);
    const isViewMode = surveyState.survey.surveyMode === MODE.VIEW;
    const isOverViewMode = surveyState.survey.surveyMode === MODE.OVERVIEW;

    // Memoized survey summary data
    const surveySummaryMasterData = useMemo(() => {
        return surveyMasterData.map((item) => ({
            partnerNo: item?.surveySummaryRecord?.partnerNo,
            partnerName: item?.surveySummaryRecord?.partnerName,
            companyCode: item?.surveySummaryRecord?.companyCode,
        }));
    }, [surveyMasterData]);

    // Internationalized messages
    const messages = useMemo(
        () => ({
            warning: intl.formatMessage({
                id: 'survey-eDocument-supplier-defaultAccept-message',
                defaultMessage: 'You have decided to accept the BMW default settings...',
            }),
            headerLines: [
                intl.formatMessage({ id: 'survey-eDocument-customer-headerText-line1' }),
                intl.formatMessage({ id: 'survey-eDocument-customer-headerText-line2' }),
                intl.formatMessage({ id: 'survey-eDocument-customer-headerText-line3' }),
            ],
            acceptDefaultTooltip: intl.formatMessage({
                id: 'survey-eDocument-customer-grid-acceptDefault-tooltip-title',
                defaultMessage: 'Accept Default',
            }),
            eDocumentTitle: intl.formatMessage({
                id: 'survey-eDocument-customer-title',
                defaultMessage: 'Document Types',
            }),
            editMessage: intl.formatMessage({ id: 'survey-eDocument-customer-edit-messages' }),
            editTooltip: intl.formatMessage({
                id: 'survey-eDocument-customer-grid-editChannel-tooltip-title',
                defaultMessage: 'Edit All',
            }),
            saveTooltip: intl.formatMessage({
                id: 'survey-eDocument-customer-grid-saveChannel-tooltip-title',
                defaultMessage: 'Save',
            }),
            definitions: intl.formatMessage({
                id: 'guidelines-and-testfiles',
                defaultMessage: 'Guidelines and testfiles',
            }),
            updatedChannelLabel: intl.formatMessage({
                id: 'survey-eDocument-customer-updatedChannel-label',
                defaultMessage: 'Updated Channel',
            }),
            updatedChannelPlaceholder: intl.formatMessage({
                id: 'survey-eDocument-customer-updatedChannel-placeholder',
                defaultMessage: 'Select',
            }),
            updatedFormatLabel: intl.formatMessage({
                id: 'survey-eDocument-customer-updatedFormat-label',
                defaultMessage: 'Updated Format',
            }),
            updatedFormatPlaceholder: intl.formatMessage({
                id: 'survey-eDocument-customer-updatedFormat-placeholder',
                defaultMessage: 'Select',
            }),
        }),
        [intl]
    );

    // Fetch channel data on mount
    useEffect(() => {
        const fetchChannelData = async () => {
            if (!surveyMasterData.length || channelData) return;

            setLoading(true);
            try {
                const partnerType = localStorage.getItem(PARTNER_TYPE);
                const companyCode = surveyMasterData[0].surveySummaryRecord.companyCode;
                const surveyId = surveyMasterData[0].surveySummaryRecord.surveyId;

                let customerChannels = await getSurveyChannelBySurveyId(surveyId, partnerType, companyCode, intl);

                if (!Array.isArray(customerChannels) || customerChannels.length === 0) {
                    const channels = await getDefaultChannelData(partnerType, companyCode);
                    customerChannels = await getProcessName(channels, intl);
                }

                if (Array.isArray(customerChannels)) {
                    setChannelData(customerChannels as ChannelData[]);
                } else {
                    console.error('Invalid channel data type:', customerChannels);
                }
            } catch (error) {
                console.error('Failed to fetch channel data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchChannelData();
    }, [surveyMasterData, channelData, intl]);

    const onDocumentCustomerTypeGridReady = useCallback((params: { api: GridApi }) => {
        setGridApi(params.api);
        params.api.sizeColumnsToFit();
    }, []);

    const editChannel = useCallback(async () => {
        setLoading(true);
        try {
            if (!channelData) return;

            const selectedProcessCode = channelData.find(
                (item) => item.processCode !== 'INC' && item.processCode !== 'PAD'
            );

            if (selectedProcessCode) {
                const partnerType = localStorage.getItem(PARTNER_TYPE);
                const companyCode = surveyMasterData[0].surveySummaryRecord.companyCode;

                const returnedFormats = await getFormats(
                    partnerType,
                    companyCode,
                    selectedProcessCode.processCode,
                    'OUT'
                );

                const formats = returnedFormats.format.map((item) => item.formatName);
                setFormatOptions(formats);
                setChannelFormatOptionData(returnedFormats);
                setShowEditPanel(true);
            }
        } catch (error) {
            console.error('Failed to edit channel:', error);
        } finally {
            setLoading(false);
        }
    }, [channelData, surveyMasterData]);

    const saveChannel = useCallback(async () => {
        if (!updatedFormat || !updatedChannel || !surveyMasterData.length || !channelData) {
            showErrorWithHeaderNotification(
                intl.formatMessage({
                    id: 'survey-eDocument-customer-channel-not-selected-error',
                    defaultMessage: 'Fail',
                }),
                intl.formatMessage({
                    id: 'survey-eDocument-customer-channel-not-selected-error-message',
                    defaultMessage: 'Please select Format and Channels to update',
                })
            );
            return;
        }

        setLoading(true);
        try {
            const inputData = channelData.map((item) => ({
                surveyId: Number(surveyMasterData[0].surveySummaryRecord.surveyId),
                surveyChannelId: item.surveyChannelId,
                processCodeId: item.processCodeId,
                documentType: item.documentType,
                format: updatedFormat,
                channel: updatedChannel,
                provider: selectedProvider,
                channelDefault: true,
            }));

            const insertedData = await insertSurveyChannels(
                surveyMasterData[0].surveySummaryRecord?.includedSurveyIds,
                inputData
            );

            // @ts-ignore
            const updatedChannelData = insertedData?.map((item: { channel: any; format: any }) => ({
                ...item,
                updatedChannel: item.channel,
                updatedFormat: item.format,
                channel: item.channel,
                format: item.format,
            }));

            form.setFieldsValue({
                format: updatedChannelData[0]?.format || 'PDF',
                channel: updatedChannelData[0]?.channel || 'PORTAL',
            });

            setChannelData(updatedChannelData);

            notification.info({
                message: intl.formatMessage({
                    id: 'survey-eDocument-supplier-success-notification-header',
                    defaultMessage: 'SUCCESSFULLY',
                }),
                description: intl.formatMessage({
                    id: 'survey-eDocument-customer-saved-channel-successfully',
                    defaultMessage: 'Format and Channels successfully saved',
                }),
            });

            onStatusUpdate?.();
        } catch (error) {
            notification.error({
                message: intl.formatMessage({
                    id: 'survey-eDocument-supplier-error-notification-header',
                    defaultMessage: 'ERROR',
                }),
                description: intl.formatMessage({
                    id: 'survey-eDocument-customer-saved-channel-error',
                    defaultMessage: String(error),
                }),
            });
        } finally {
            setLoading(false);
        }
    }, [updatedFormat, updatedChannel, surveyMasterData, channelData, selectedProvider, form, intl, onStatusUpdate]);

    const onChannelChange = useCallback(
        (value: string) => {
            setUpdatedChannel(value);
            if (!channelFormatOptionData || !updatedFormat) return;

            const selectedFormat = channelFormatOptionData.format.find((item) => item.formatName === updatedFormat);

            const selectedChannel = selectedFormat?.channel.find((channelItem) => channelItem.channelName === value);

            if (selectedChannel) {
                setSelectedProvider(selectedChannel.provider);
            }
        },
        [channelFormatOptionData, updatedFormat]
    );

    const onFormatChange = useCallback(
        (value: string) => {
            form.setFieldsValue({ updatedChannel: undefined });
            setUpdatedChannel(undefined);
            setUpdatedFormat(value);

            if (!channelFormatOptionData) return;

            const selectedFormat = channelFormatOptionData.format.find((item) => item.formatName === value);

            const channels = selectedFormat?.channel.map((channelData) => channelData.channelName) || [];

            setChannelOptions(channels);
        },
        [channelFormatOptionData, form]
    );

    const initialFormValues = useMemo(
        () => ({
            format: channelData?.[0]?.format || 'PDF',
            channel: channelData?.[0]?.channel || 'PORTAL',
        }),
        [channelData]
    );

    return (
        <ConfigProvider locale={localStorage.getItem('locale') === 'en' ? enUS : deDe}>
            <Spin spinning={loading} size="large">
                <Card title={messages.eDocumentTitle} size="small" className="ml-10">
                    <Collapse className={'mb20'}>
                        <Collapse.Panel
                            key={1}
                            id="Panel1"
                            header={getI18n('Show-Description', 'Show Description', intl)}
                            style={{ marginTop: 'auto' }}
                        >
                            <Row>
                                <Col>
                                    <Paragraph type={"secondary"}>GroupId: {groupId}</Paragraph>
                                </Col>
                            </Row>
                            <Typography>
                                <Typography.Paragraph>
                                    {messages.headerLines.map((line, index) => (
                                        <Row key={index}>{line}</Row>
                                    ))}
                                </Typography.Paragraph>
                            </Typography>

                            <Row gutter={24}>
                                <Col span={24} className="mb5">
                                    <QuestionCircleOutlined
                                        style={{ fontSize: '20px', color: '#444444' }}
                                        className="mr10"
                                    />
                                    <Typography.Link href={getDefinitionFormatChannelHelp()} target="_blank">
                                        {messages.definitions}
                                    </Typography.Link>
                                </Col>
                            </Row>
                        </Collapse.Panel>
                    </Collapse>

                    {!(isViewMode || isOverViewMode) && (
                        <Row gutter={24} justify="start">
                            <Col className="mb5">
                                <span style={{ marginRight: '20px', marginTop: '20px' }}>
                                    <Link to={'/survey/survey-overview'}>
                                        <Tooltip
                                            title={getI18n(
                                                'survey-master-data-partner-tab-back-icon-tool-tip',
                                                'Return to Survey Overview Page',
                                                intl
                                            )}
                                            key={'keyCancelInvoiceIcon'}
                                        >
                                            <HomeOutlined
                                                style={{
                                                    fontSize: '25px',
                                                    cursor: 'pointer',
                                                    color: '#024051',
                                                    marginTop: '15px',
                                                }}
                                            />
                                        </Tooltip>
                                    </Link>
                                </span>
                                <Tooltip title={messages.editTooltip}>
                                    <EditOutlined
                                        style={{ fontSize: '25px', color: '#024051', cursor: 'pointer' }}
                                        onClick={editChannel}
                                    />
                                </Tooltip>
                                <Tooltip title={messages.saveTooltip}>
                                    <SaveOutlined
                                        style={{ fontSize: '25px', color: '#024051', cursor: 'pointer' }}
                                        className="ml10"
                                        onClick={saveChannel}
                                    />
                                </Tooltip>
                            </Col>
                        </Row>
                    )}

                    <Row gutter={24}>
                        <Col span={24} className="mb5">
                            <div style={{ flex: 'auto', height: '200px', paddingTop: '0px' }}>
                                <AgGridReact
                                    defaultColDef={{
                                        enableValue: true,
                                        sortable: true,
                                        resizable: true,
                                    }}
                                    rowData={surveySummaryMasterData}
                                    columnDefs={SURVEY_CUSTOMER_DOCUMENT_PARTNER_TABLE_HEADERS(intl)}
                                    localeText={getAgGridLocalization(intl)}
                                    sideBar={false}
                                    rowClass="agGridRows"
                                    suppressMenuHide={true}
                                    pagination={true}
                                    paginationPageSize={5}
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col span={24} className="mb5">
                            <div style={{ flex: 'auto', height: '350px', paddingTop: '10px' }}>
                                <AgGridReact
                                    defaultColDef={DEFAULT_COL_DEF}
                                    rowData={channelData}
                                    columnDefs={SURVEY_DOCUMENT_CHANNEL_TABLE_HEADERS(intl)}
                                    sideBar={false}
                                    rowClass="agGridRows"
                                    suppressMenuHide={true}
                                    pagination={true}
                                    localeText={getAgGridLocalization(intl)}
                                    paginationPageSize={10}
                                    onGridReady={onDocumentCustomerTypeGridReady}
                                />
                            </div>
                        </Col>
                    </Row>

                    {showEditPanel && (
                        <Card size="small">
                            <Typography>
                                <Typography.Paragraph>{messages.editMessage}</Typography.Paragraph>
                            </Typography>
                            <Form
                                size="middle"
                                layout="vertical"
                                form={form}
                                initialValues={initialFormValues}
                                onFinish={saveChannel}
                            >
                                <Row gutter={24}>
                                    <Col span={3}>
                                        <label>
                                            {intl.formatMessage({
                                                id: 'survey-supplier-doc-type-simple-grid-header-format',
                                            })}
                                        </label>
                                        <Form.Item name="format">
                                            <Input disabled={true} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={3}>
                                        <label>
                                            {intl.formatMessage({
                                                id: 'survey-supplier-doc-type-simple-grid-header-channel',
                                            })}
                                        </label>
                                        <Form.Item name="channel">
                                            <Input disabled={true} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={3}>
                                        <label>{messages.updatedFormatLabel}</label>
                                        <Form.Item name="updatedFormat">
                                            <Select
                                                placeholder={messages.updatedFormatPlaceholder}
                                                allowClear={true}
                                                optionFilterProp="children"
                                                showSearch={true}
                                                onChange={onFormatChange}
                                            >
                                                {formatOptions.map((item, index) => (
                                                    <Select.Option key={`${item}_${index}`} value={item}>
                                                        {item}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={3}>
                                        <label>{messages.updatedChannelLabel}</label>
                                        <Form.Item
                                            name="updatedChannel"
                                            rules={[{ required: true, message: 'Please select document format!' }]}
                                        >
                                            <Select
                                                placeholder={messages.updatedChannelPlaceholder}
                                                allowClear={true}
                                                optionFilterProp="children"
                                                showSearch={true}
                                                onChange={onChannelChange}
                                                disabled={!updatedFormat}
                                            >
                                                {channelOptions.map((item, index) => (
                                                    <Select.Option key={`${item}_${index}`} value={item}>
                                                        {item}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>
                        </Card>
                    )}
                </Card>
            </Spin>
        </ConfigProvider>
    );
};

export default SurveyCustomerDocumentType;
